import { Segment } from "@helpers/segment";

export interface SegmentWithSpacing extends Segment {
    width: number;
    gapWidth: string;
    showGap: boolean;
}

export function redistributeWidths(
    data: SegmentWithSpacing[],
    N: number = 1,
): SegmentWithSpacing[] {
    const result = [...data];
    const toBoostIndices: number[] = [];

    // Step 1: Find indices to boost and apply minimum width
    result.forEach((chunk, i) => {
        if (chunk.value > 0 && chunk.width < N) {
            result[i] = { ...chunk, width: N };
            toBoostIndices.push(i);
        }
    });

    const totalBoost = toBoostIndices.length * N;

    if (totalBoost === 0) {
        return result;
    }

    // Step 2: Reduce from chunks with width > N and not in toBoostIndices
    const adjustableIndices = result
        .map((chunk, i) => ({ ...chunk, index: i }))
        .filter(
            (item) => item.width > N && !toBoostIndices.includes(item.index),
        );

    const totalAdjustable = adjustableIndices.reduce(
        (sum, item) => sum + (item.width - N),
        0,
    );

    if (totalAdjustable < totalBoost) {
        throw new Error("Not enough width to redistribute.");
    }

    adjustableIndices.forEach(({ index, width }) => {
        const share = (width - N) / totalAdjustable;
        const reduction = share * totalBoost;
        result[index].width = width - reduction;
    });

    // Optional rounding correction
    const sum = result.reduce((s, c) => s + c.width, 0);
    const diff = sum - 100;
    if (Math.abs(diff) > 0.01) {
        const maxIdx = result.reduce(
            // eslint-disable-next-line max-params
            (maxI, c, i, arr) => (c.width > arr[maxI].width ? i : maxI),
            0,
        );
        result[maxIdx].width -= diff;
    }

    return result;
}
